import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, AlertTriangle, Zap, BookOpen } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useContent } from "@/lib/ContentContext";
import SEO from "@/components/SEO";
import { useProducts } from "@/lib/ProductsContext";
import { EducationalDisclaimer } from "@/components/LegalNotices";
import { getGeneratedArticle, trackEventBeacon } from "@/lib/api";
import { detectToolForArticle } from "@/lib/articleToolRouting";
import RelatedArticles from "@/components/RelatedArticles";
import { MidArticleCTA, EndArticleCTA } from "@/components/InlineArticleCTA";
import ArticleTags from "@/components/ArticleTags";
import { ContentUpgradeCTA, shouldShowContentUpgrade } from "@/components/ContentUpgradeCTA";
import Breadcrumbs from "@/components/Breadcrumbs";
import StickyArticleCTA from "@/components/StickyArticleCTA";
import TrackerCTA from "@/components/TrackerCTA";

const SEOPage = () => {
  const { guides: GUIDES, bundle: BUNDLE, tools: TOOLS, freebies: FREEBIES } = useProducts();
  const { seoContent: SEO_CONTENT, articleHubs: ARTICLE_HUBS } = useContent();
  const { slug } = useParams();
  const [dynamicArticle, setDynamicArticle] = useState(null);
  const staticPageData = SEO_CONTENT[slug];

  useEffect(() => {
    if (!staticPageData) {
      getGeneratedArticle(slug).then(res => setDynamicArticle(res.data)).catch(() => {});
    }
  }, [slug, staticPageData]);

  const pageData = staticPageData || dynamicArticle;

  if (!pageData) {
    return (
      <div className="section-padding container-main text-center">
        <h1>Article not found</h1>
        <Link to="/glp-guide" className="btn-primary mt-4">
          Browse All Articles
        </Link>
      </div>
    );
  }

  // Prefer explicit relatedTool, else auto-detect from slug/title/keyword so
  // every article routes readers into its matching diagnostic tool funnel.
  const detectedToolKey = detectToolForArticle({ ...pageData, slug });
  const relatedTool = TOOLS[pageData.relatedTool] || TOOLS[detectedToolKey];
  const relatedGuide = GUIDES[pageData.relatedGuide];
  const isNewFormat = !!pageData.content.hook;
  const isLongForm = !!pageData.long_form_markdown;
  const relatedFreebie = pageData.relatedFreebie ? FREEBIES[pageData.relatedFreebie] : null;
  const hub = pageData.hub ? ARTICLE_HUBS[pageData.hub] : null;
  const linkedArticles = (pageData.relatedArticles || [])
    .map(s => SEO_CONTENT[s])
    .filter(Boolean);

  // Find the hub this article belongs to (for old articles without hub field)
  const articleHub = hub || Object.values(ARTICLE_HUBS).find(h => h.articles.includes(slug));
  const showUpgrade = shouldShowContentUpgrade(pageData);

  return (
    <div className="noise-bg">
      <SEO title={pageData.title} description={pageData.metaDescription || pageData.description} canonical={`https://metaboliclabos.com/articles/${slug}`} />
      {/* Article Header */}
      <section className="gradient-hero section-padding">
        <div className="container-main">
          <div className="max-w-3xl">
            <Breadcrumbs items={[
              { label: "Resources", href: "/glp-guide" },
              ...(articleHub ? [{ label: articleHub.title, href: `/${articleHub.slug || 'glp-guide'}` }] : []),
              { label: pageData.title }
            ]} />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <h1 className="text-5xl md:text-6xl lg:text-7xl mb-4 leading-[1.02] tracking-tight" data-testid="seo-title">
                {pageData.title}
              </h1>
              <div className="mb-4" data-testid="article-byline">
                <p className="text-sm text-[#838F80]">By the GLP-1 Lab OS Editorial Team</p>
                <p className="text-xs text-[#A8B0A0]">Reviewed for educational accuracy | MetabolicLabOS.com</p>
              </div>
              <p className="text-xl text-[#4B5548]">
                {pageData.humanized_intro || (isNewFormat ? pageData.content.hook : pageData.content.intro)}
              </p>
              <ArticleTags article={pageData} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                {isLongForm ? (
                  <>
                    <div className="prose prose-lg max-w-none article-longform" data-testid="article-longform">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h2: ({node, ...props}) => <h2 className="text-2xl md:text-3xl font-semibold text-[#1A2018] mt-10 mb-4" {...props} />,
                          h3: ({node, ...props}) => <h3 className="text-xl font-semibold text-[#1A2018] mt-6 mb-3" {...props} />,
                          p: ({node, ...props}) => <p className="text-lg text-[#4B5548] leading-relaxed mb-4" {...props} />,
                          ul: ({node, ...props}) => <ul className="list-disc pl-6 space-y-2 mb-6 text-lg text-[#4B5548]" {...props} />,
                          ol: ({node, ...props}) => <ol className="list-decimal pl-6 space-y-2 mb-6 text-lg text-[#4B5548]" {...props} />,
                          li: ({node, ...props}) => <li className="leading-relaxed" {...props} />,
                          strong: ({node, ...props}) => <strong className="font-semibold text-[#1A2018]" {...props} />,
                          a: ({node, href, ...props}) => {
                            const internal = typeof href === "string" && href.startsWith("/");
                            return internal
                              ? <Link to={href} className="text-[#2D6A4F] underline hover:text-[#1B4332] font-medium" {...props} />
                              : <a href={href} className="text-[#2D6A4F] underline hover:text-[#1B4332] font-medium" target="_blank" rel="noopener noreferrer" {...props} />;
                          },
                          blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-[#2D6A4F] pl-4 italic text-[#4B5548] my-6" {...props} />,
                        }}
                      >
                        {pageData.long_form_markdown}
                      </ReactMarkdown>
                    </div>

                    {/* Mid-article CTA injected once after the markdown */}
                    <MidArticleCTA tool={relatedTool} freebie={relatedFreebie} article={pageData} guides={GUIDES} />
                    {showUpgrade && <ContentUpgradeCTA position="end" />}

                    <TrackerCTA variant="inline" source="article-longform" />

                    <EducationalDisclaimer className="mt-6" />
                  </>
                ) : isNewFormat ? (
                  <>
                    {/* Why This Happens */}
                    <div className="mb-10">
                      <h2 className="text-2xl font-semibold text-[#1A2018] mb-4 flex items-center gap-3">
                        <AlertTriangle className="w-6 h-6 text-[#E07A5F]" />
                        Why This Happens
                      </h2>
                      <p className="text-lg text-[#4B5548] leading-relaxed">{pageData.content.whyThisHappens}</p>
                    </div>

                    {/* Quick Fixes */}
                    <div className="mb-10">
                      <h2 className="text-2xl font-semibold text-[#1A2018] mb-6 flex items-center gap-3">
                        <Zap className="w-6 h-6 text-[#2D6A4F]" />
                        Quick Fixes
                      </h2>
                      {pageData.content.quickFixes.map((fix, index) => (
                        <div key={index} className="mb-8">
                          <div className="flex items-start gap-4 mb-3">
                            <div className="w-10 h-10 rounded-full bg-[#2D6A4F] flex items-center justify-center flex-shrink-0">
                              <span className="text-white font-bold">{index + 1}</span>
                            </div>
                            <h3 className="text-xl font-semibold text-[#1A2018]">{fix.title}</h3>
                          </div>
                          <p className="text-lg text-[#4B5548] ml-14">{fix.description}</p>
                        </div>
                      ))}
                    </div>

                    {/* Mid-Article CTA */}
                    <MidArticleCTA tool={relatedTool} freebie={relatedFreebie} article={pageData} guides={GUIDES} />
                    {showUpgrade && <ContentUpgradeCTA position="mid" />}

                    {/* Deeper Fix */}
                    <div className="mb-10">
                      <h2 className="text-2xl font-semibold text-[#1A2018] mb-4 flex items-center gap-3">
                        <BookOpen className="w-6 h-6 text-[#D4A373]" />
                        The Deeper Fix
                      </h2>
                      <p className="text-lg text-[#4B5548] leading-relaxed">{pageData.content.deeperFix}</p>
                    </div>

                    {/* Conclusion + End CTA */}
                    <EndArticleCTA guide={relatedGuide} conclusion={pageData.humanized_conclusion || pageData.content.conclusion} article={pageData} guides={GUIDES} />
                    {showUpgrade && <ContentUpgradeCTA position="end" />}

                    <TrackerCTA variant="inline" source="article-newformat" />

                    {/* Disclaimer */}
                    <EducationalDisclaimer className="mt-6" />

                    {/* Internal Links */}
                    {linkedArticles.length > 0 && (
                      <div className="mt-10 p-6 bg-[#F2F0E9] rounded-2xl">
                        <h3 className="font-semibold text-[#1A2018] mb-4">Related Reading</h3>
                        <div className="space-y-3">
                          {linkedArticles.map(article => (
                            <Link
                              key={article.slug}
                              to={`/articles/${article.slug}`}
                              className="flex items-center gap-3 text-[#2D6A4F] hover:text-[#1B4332] font-medium"
                            >
                              <ArrowRight className="w-4 h-4 flex-shrink-0" />
                              {article.title}
                            </Link>
                          ))}
                          {articleHub && (
                            <Link
                              to={`/${articleHub.slug}`}
                              className="flex items-center gap-3 text-[#4B5548] hover:text-[#1A2018] font-medium mt-2 pt-2 border-t border-gray-200"
                            >
                              <ArrowRight className="w-4 h-4 flex-shrink-0" />
                              Browse all {articleHub.shortTitle || articleHub.title} articles
                            </Link>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  /* Old format rendering */
                  <>
                    {pageData.content.steps.map((step, index) => (
                      <div key={index} className="mb-10">
                        <div className="flex items-start gap-4 mb-4">
                          <div className="w-10 h-10 rounded-full bg-[#2D6A4F] flex items-center justify-center flex-shrink-0">
                            <span className="text-white font-bold">{index + 1}</span>
                          </div>
                          <h2 className="text-2xl font-semibold text-[#1A2018]">{step.title}</h2>
                        </div>
                        <p className="text-lg text-[#4B5548] ml-14">{step.description}</p>
                        {/* Insert mid-article CTA after 3rd step */}
                        {index === 2 && <MidArticleCTA tool={relatedTool} freebie={relatedFreebie} article={pageData} guides={GUIDES} />}
                      </div>
                    ))}

                    <EndArticleCTA guide={relatedGuide} conclusion={pageData.humanized_conclusion || pageData.content.conclusion} article={pageData} guides={GUIDES} />
                    {showUpgrade && <ContentUpgradeCTA position="end" />}

                    <TrackerCTA variant="inline" source="article-legacy" />

                    <EducationalDisclaimer className="mt-6" />
                  </>
                )}
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-28 space-y-6">
                {/* Related Tool CTA */}
                {relatedTool && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="card-elevated p-6"
                  >
                    <span className="inline-block px-3 py-1 bg-[#E9EDC9] text-[#2D6A4F] rounded-full text-xs font-medium mb-3">
                      Free Tool
                    </span>
                    <h3 className="text-lg font-semibold mb-2 text-[#1A2018]">{relatedTool.title}</h3>
                    <p className="text-sm text-[#4B5548] mb-4">{relatedTool.description}</p>
                    <Link
                      to={`/${relatedTool.slug}`}
                      onClick={() =>
                        trackEventBeacon("article_to_tool_clicked", {
                          article_slug: slug,
                          tool_id: relatedTool.id || detectedToolKey,
                          placement: "sidebar",
                        })
                      }
                      className="btn-secondary w-full justify-center text-sm py-3"
                      data-testid="sidebar-tool-btn"
                    >
                      Try the Free Tool
                    </Link>
                  </motion.div>
                )}

                {/* Ask Kai about this article — Item 6, direct article → Kai edge */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="card-elevated p-6"
                >
                  <span className="inline-block px-3 py-1 bg-[#2D6A4F]/10 text-[#2D6A4F] rounded-full text-xs font-medium mb-3">
                    Ask Kai
                  </span>
                  <h3 className="text-lg font-semibold mb-2 text-[#1A2018]">
                    Talk this through with Kai
                  </h3>
                  <p className="text-sm text-[#4B5548] mb-4">
                    Kai can help you make sense of what you just read — free daily messages, no re-typing needed.
                  </p>
                  <Link
                    to={`/kai?from_article=${encodeURIComponent(slug)}`}
                    onClick={() =>
                      trackEventBeacon("article_to_kai_clicked", {
                        article_slug: slug,
                        placement: "sidebar",
                      })
                    }
                    className="btn-primary w-full justify-center text-sm py-3"
                    data-testid="sidebar-ask-kai-btn"
                  >
                    Ask Kai About This
                  </Link>
                </motion.div>

                {/* Related Guide CTA */}
                {relatedGuide && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="card-elevated p-6"
                  >
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${
                      relatedGuide.category === "core"
                        ? "bg-[#E07A5F]/10 text-[#E07A5F]"
                        : "bg-[#2D6A4F]/10 text-[#2D6A4F]"
                    }`}>
                      {relatedGuide.category === "core" ? "Core Guide" : "Supporting Guide"}
                    </span>
                    <h3 className="text-lg font-semibold mb-2 text-[#1A2018]">{relatedGuide.title}</h3>
                    <p className="text-sm text-[#4B5548] mb-4">{relatedGuide.description}</p>
                    <Link
                      to={`/guides/${relatedGuide.slug}`}
                      className="text-[#2D6A4F] font-medium text-sm flex items-center gap-1"
                      data-testid="sidebar-guide-btn"
                    >
                      Learn more <ArrowRight className="w-4 h-4" />
                    </Link>
                  </motion.div>
                )}

                {/* Bundle CTA */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="card-elevated p-6 border-2 border-[#2D6A4F]"
                >
                  <span className="badge-best-value mb-3 inline-block text-xs">Best Value</span>
                  <h3 className="text-lg font-semibold mb-2 text-[#1A2018]">Get the Complete System</h3>
                  <p className="text-sm text-[#4B5548] mb-4">
                    All 8 guides for just ${BUNDLE?.price || 49} (normally ${BUNDLE?.original_value || 127})
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="flex items-center gap-2 text-sm text-[#4B5548]">
                      <Check className="w-4 h-4 text-[#2D6A4F]" />
                      Address all side effects
                    </li>
                    <li className="flex items-center gap-2 text-sm text-[#4B5548]">
                      <Check className="w-4 h-4 text-[#2D6A4F]" />
                      Build sustainable routines
                    </li>
                    <li className="flex items-center gap-2 text-sm text-[#4B5548]">
                      <Check className="w-4 h-4 text-[#2D6A4F]" />
                      Support your progress
                    </li>
                  </ul>
                  <Link
                    to="/glp-survival-system"
                    className="btn-primary w-full justify-center text-sm py-3"
                    data-testid="sidebar-bundle-btn"
                  >
                    Get All 8 Guides
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Related Articles */}
      <RelatedArticles currentSlug={slug} count={4} />

      {/* Sticky mobile CTA */}
      <StickyArticleCTA />
    </div>
  );
};

export default SEOPage;
